import { useEffect } from "react";
import { SITE_URL, ORG_ID } from "../../data/entityGraph";

/**
 * ProductSchema - The Personal Action Plan.
 *
 * Injects ONE Product node for "The Personal Action Plan" with a
 * scoped @id of /start-here#personal-action-plan. This component
 * must be mounted on /start-here ONLY. Earlier versions also
 * injected this Product on / and on /book, which caused Google to
 * merge multiple schema fragments under the same shared @id and
 * report "Duplicate field brand" on the Merchant Listing in Search
 * Console. The fix is route-scoping the @id and unmounting the
 * component from every surface that is not the product page.
 *
 * Each merchant field appears exactly once:
 *   - brand     (inline Brand object)
 *   - offers    (single Offer)
 *   - seller    (@id reference, inside offers)
 *   - hasMerchantReturnPolicy (single MerchantReturnPolicy, inside offers)
 *
 * shippingDetails is intentionally omitted. The Personal Action
 * Plan is a digital personalized synthesis with no physical
 * shipping policy to represent.
 */
const PRODUCT_LD = {
  "@context": "https://schema.org",
  "@type": "Product",
  "@id": `${SITE_URL}/start-here#personal-action-plan`,
  name: "The Personal Action Plan | Authentic Momentum",
  description:
    "Guided intake plus AI-assisted personalized synthesis of your aligned next move.",
  image: [`${SITE_URL}/assets/brand/personal-action-plan-cover.png`],
  url: `${SITE_URL}/start-here`,
  brand: {
    "@type": "Brand",
    name: "Authentic Momentum",
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/assets/logo.png`,
  },
  offers: {
    "@type": "Offer",
    price: "49.99",
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    itemCondition: "https://schema.org/NewCondition",
    url: `${SITE_URL}/start-here`,
    seller: { "@id": ORG_ID },
    hasMerchantReturnPolicy: {
      "@type": "MerchantReturnPolicy",
      applicableCountry: "US",
      returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
    },
  },
};

const TAG = "data-am-product-schema";

const ProductSchema = () => {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute(TAG, "personal-action-plan");
    el.textContent = JSON.stringify(PRODUCT_LD);
    document.head.appendChild(el);
    return () => {
      el.remove();
    };
  }, []);
  return null;
};

export default ProductSchema;
